import React, { useState } from 'react';
import { X, Eye, Globe, Clock, TrendingUp } from 'lucide-react';

export const SEGMENT_TYPES = [
    { value: 'custom', label: 'Custom Segment' },
    { value: 'auto_generated', label: 'Auto-Generated' },
    { value: 'high_intent', label: 'High Intent' }
];

export const FUNNEL_STAGES = [
    { value: 'TOFU', label: 'Awareness (TOFU)', color: 'var(--color-info)' },
    { value: 'MOFU', label: 'Consideration (MOFU)', color: 'var(--color-warning)' },
    { value: 'BOFU', label: 'Conversion (BOFU)', color: 'var(--color-success)' }
];

/**
 * SegmentForm - Reusable form component for Segment node editing
 * Used in both SegmentModal and PropertiesPanel
 */
const SegmentForm = ({
    formData,
    onChange,
    errors = {}
}) => {
    const [pageInput, setPageInput] = useState('');

    const handleChange = (field, value) => {
        onChange(field, value);
    };

    const toggleSourceStage = (stage) => {
        const current = formData.sourceStages || ['TOFU'];
        const updated = current.includes(stage)
            ? current.filter(s => s !== stage)
            : [...current, stage];
        if (updated.length > 0) {
            handleChange('sourceStages', updated);
        }
    };

    const handleSignalChange = (signalType, field, value) => {
        const intentSignals = formData.intentSignals || getDefaultIntentSignals();
        onChange('intentSignals', {
            ...intentSignals,
            [signalType]: {
                ...intentSignals[signalType],
                [field]: value
            }
        });
    };

    const toggleSignal = (signalType) => {
        const intentSignals = formData.intentSignals || getDefaultIntentSignals();
        handleSignalChange(signalType, 'enabled', !intentSignals[signalType].enabled);
    };

    const addPage = () => {
        const intentSignals = formData.intentSignals || getDefaultIntentSignals();
        if (pageInput.trim() && !intentSignals.websiteBehavior.pagesVisited.includes(pageInput.trim())) {
            handleSignalChange('websiteBehavior', 'pagesVisited', [
                ...intentSignals.websiteBehavior.pagesVisited,
                pageInput.trim()
            ]);
            setPageInput('');
        }
    };

    const removePage = (page) => {
        const intentSignals = formData.intentSignals || getDefaultIntentSignals();
        handleSignalChange('websiteBehavior', 'pagesVisited',
            intentSignals.websiteBehavior.pagesVisited.filter(p => p !== page)
        );
    };

    const intentSignals = formData.intentSignals || getDefaultIntentSignals();
    const enabledCount = Object.values(intentSignals).filter(s => s.enabled).length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Segment Name */}
            <div className="input-group">
                <label className="input-label">Segment Name *</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., High-Intent Browsers, Ready to Convert"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    style={errors.name ? { borderColor: 'var(--color-error)' } : {}}
                />
                {errors.name && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.name}
                    </span>
                )}
            </div>

            {/* Segment Type & Target Stage */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <div className="input-group">
                    <label className="input-label">Segment Type</label>
                    <select
                        className="select-field"
                        value={formData.segmentType || 'custom'}
                        onChange={(e) => handleChange('segmentType', e.target.value)}
                    >
                        {SEGMENT_TYPES.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                    </select>
                </div>
                <div className="input-group">
                    <label className="input-label">Target Stage</label>
                    <select
                        className="select-field"
                        value={formData.funnelStage || 'MOFU'}
                        onChange={(e) => handleChange('funnelStage', e.target.value)}
                    >
                        {FUNNEL_STAGES.map(stage => (
                            <option key={stage.value} value={stage.value}>{stage.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Source Stages */}
            <div className="input-group">
                <label className="input-label">Source Stages</label>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', margin: '0 0 var(--space-2) 0' }}>
                    Which funnel stages feed into this segment?
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {FUNNEL_STAGES.map(stage => (
                        <button
                            key={stage.value}
                            type="button"
                            onClick={() => toggleSourceStage(stage.value)}
                            style={{
                                padding: 'var(--space-2) var(--space-4)',
                                background: (formData.sourceStages || ['TOFU']).includes(stage.value)
                                    ? stage.color
                                    : 'var(--color-bg-secondary)',
                                border: `1px solid ${(formData.sourceStages || ['TOFU']).includes(stage.value) ? stage.color : 'var(--color-border)'}`,
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                color: (formData.sourceStages || ['TOFU']).includes(stage.value) ? 'white' : 'var(--color-text-primary)',
                                fontWeight: (formData.sourceStages || ['TOFU']).includes(stage.value) ? 600 : 400
                            }}
                        >
                            {stage.value}
                        </button>
                    ))}
                </div>
                {errors.sourceStages && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.sourceStages}
                    </span>
                )}
            </div>

            {/* Intent Signals Section */}
            <div style={{
                padding: 'var(--space-4)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-lg)'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-3)'
                }}>
                    <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--color-text-primary)' }}>Intent Signals</h3>
                    <span style={{
                        padding: '2px 8px',
                        background: enabledCount >= 3 ? 'var(--color-success)' : enabledCount >= 2 ? 'var(--color-warning)' : 'var(--color-text-tertiary)',
                        color: 'white',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)'
                    }}>
                        {enabledCount} active
                    </span>
                </div>

                {/* Engagement Depth */}
                <SignalSection
                    icon={Eye}
                    label="Engagement Depth"
                    color="var(--color-primary)"
                    enabled={intentSignals.engagementDepth.enabled}
                    onToggle={() => toggleSignal('engagementDepth')}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
                        <div>
                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Min Ad Views</label>
                            <input
                                type="number"
                                className="input-field"
                                min="1"
                                value={intentSignals.engagementDepth.minAdViews}
                                onChange={(e) => handleSignalChange('engagementDepth', 'minAdViews', parseInt(e.target.value) || 1)}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Video Watch %</label>
                            <input
                                type="number"
                                className="input-field"
                                min="0"
                                max="100"
                                value={intentSignals.engagementDepth.minVideoWatchPercent}
                                onChange={(e) => handleSignalChange('engagementDepth', 'minVideoWatchPercent', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={intentSignals.engagementDepth.clickThrough}
                                    onChange={(e) => handleSignalChange('engagementDepth', 'clickThrough', e.target.checked)}
                                    style={{ marginRight: 'var(--space-1)' }}
                                />
                                Click-through required
                            </label>
                        </div>
                    </div>
                </SignalSection>

                {/* Website Behavior */}
                <SignalSection
                    icon={Globe}
                    label="Website Behavior"
                    color="var(--color-info)"
                    enabled={intentSignals.websiteBehavior.enabled}
                    onToggle={() => toggleSignal('websiteBehavior')}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        <div>
                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Pages Visited</label>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <input
                                    type="text"
                                    className="input-field"
                                    placeholder="/pricing, /features, /demo"
                                    value={pageInput}
                                    onChange={(e) => setPageInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPage())}
                                />
                                <button type="button" className="btn btn-secondary" onClick={addPage}>Add</button>
                            </div>
                            {intentSignals.websiteBehavior.pagesVisited.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', marginTop: 'var(--space-2)' }}>
                                    {intentSignals.websiteBehavior.pagesVisited.map(page => (
                                        <span key={page} style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)',
                                            padding: '4px 8px', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-full)', fontSize: 'var(--font-size-xs)'
                                        }}>
                                            {page}
                                            <button type="button" onClick={() => removePage(page)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
                            <div>
                                <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Min Time (sec)</label>
                                <input
                                    type="number"
                                    className="input-field"
                                    min="0"
                                    value={intentSignals.websiteBehavior.minTimeOnSite}
                                    onChange={(e) => handleSignalChange('websiteBehavior', 'minTimeOnSite', parseInt(e.target.value) || 0)}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={intentSignals.websiteBehavior.cartActivity}
                                        onChange={(e) => handleSignalChange('websiteBehavior', 'cartActivity', e.target.checked)}
                                        style={{ marginRight: 'var(--space-1)' }}
                                    />
                                    Cart activity
                                </label>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={intentSignals.websiteBehavior.formStarts}
                                        onChange={(e) => handleSignalChange('websiteBehavior', 'formStarts', e.target.checked)}
                                        style={{ marginRight: 'var(--space-1)' }}
                                    />
                                    Form starts
                                </label>
                            </div>
                        </div>
                    </div>
                </SignalSection>

                {/* Recency & Frequency */}
                <SignalSection
                    icon={Clock}
                    label="Recency & Frequency"
                    color="var(--color-warning)"
                    enabled={intentSignals.recencyFrequency.enabled}
                    onToggle={() => toggleSignal('recencyFrequency')}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                        <div>
                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Last Interaction (days)</label>
                            <input
                                type="number"
                                className="input-field"
                                min="1"
                                value={intentSignals.recencyFrequency.lastInteractionDays}
                                onChange={(e) => handleSignalChange('recencyFrequency', 'lastInteractionDays', parseInt(e.target.value) || 1)}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>Min Interactions</label>
                            <input
                                type="number"
                                className="input-field"
                                min="1"
                                value={intentSignals.recencyFrequency.minInteractions}
                                onChange={(e) => handleSignalChange('recencyFrequency', 'minInteractions', parseInt(e.target.value) || 1)}
                            />
                        </div>
                    </div>
                </SignalSection>

                {/* Stage Progression */}
                <SignalSection
                    icon={TrendingUp}
                    label="Stage Progression"
                    color="var(--color-success)"
                    enabled={intentSignals.stageProgression.enabled}
                    onToggle={() => toggleSignal('stageProgression')}
                    noMargin
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)' }}>Users who moved from</span>
                        <select
                            className="select-field"
                            style={{ width: 'auto' }}
                            value={intentSignals.stageProgression.fromStage}
                            onChange={(e) => handleSignalChange('stageProgression', 'fromStage', e.target.value)}
                        >
                            <option value="TOFU">TOFU</option>
                            <option value="MOFU">MOFU</option>
                        </select>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)' }}>to</span>
                        <select
                            className="select-field"
                            style={{ width: 'auto' }}
                            value={intentSignals.stageProgression.toStage}
                            onChange={(e) => handleSignalChange('stageProgression', 'toStage', e.target.value)}
                        >
                            <option value="MOFU">MOFU</option>
                            <option value="BOFU">BOFU</option>
                        </select>
                    </div>
                </SignalSection>
            </div>
        </div>
    );
};

// Helper component for signal sections
const SignalSection = ({ icon: Icon, label, color, enabled, onToggle, children, noMargin }) => (
    <div style={{
        padding: 'var(--space-3)',
        background: 'var(--color-bg-primary)',
        borderRadius: 'var(--radius-md)',
        marginBottom: noMargin ? 0 : 'var(--space-3)',
        border: enabled ? `1px solid ${color}` : '1px solid var(--color-border)'
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: enabled ? 'var(--space-2)' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <Icon size={16} style={{ color }} />
                <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{label}</span>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={onToggle}
                    style={{ marginRight: 'var(--space-1)' }}
                />
                Enabled
            </label>
        </div>
        {enabled && children}
    </div>
);

// Default intent signals structure
const getDefaultIntentSignals = () => ({
    engagementDepth: { enabled: true, minAdViews: 2, minVideoWatchPercent: 50, clickThrough: false },
    websiteBehavior: { enabled: false, pagesVisited: [], minTimeOnSite: 30, cartActivity: false, formStarts: false },
    recencyFrequency: { enabled: true, lastInteractionDays: 14, minInteractions: 1 },
    stageProgression: { enabled: false, fromStage: 'TOFU', toStage: 'MOFU' }
});

export default SegmentForm;
